import axios, { AxiosResponse } from "axios";
import { ApiEndpoint, API_ENDPOINT } from "./apiEndpoint";
import { jwt } from "./config";
import { toToastMessage } from "@utils/toastMessage";
import * as Sentry from "@sentry/react";
import { track } from "../../lib/tracker";
import { isPracticeMode } from "@utils/practice/practiceMode";
import { resolvePracticeAdapter } from "@utils/practice/practiceApi";
import { isUnauthorized, recoverLineSession } from "@utils/lineSession";
import { isPayLinkMode } from "@utils/payLink";

// 本機 skip-LIFF 開發模式（.env.development.local 的 REACT_APP_SKIP_LIFF_LOGIN=true）：
// 直接注入 mock JWT、完全不跑 liff.init()，因此沒有真的 lineAccessToken。
const isDevSkipLiff = process.env.NODE_ENV === "development" && process.env.REACT_APP_SKIP_LIFF_LOGIN === "true";

// 定義異步請求的類型
type AsyncRequest<T> = () => Promise<AxiosResponse<T>>;
// 定義成功和失敗處理的回調類型
type SuccessCallback<T> = (response: AxiosResponse<T>) => void | Promise<void>;
type ErrorCallback = (error: unknown) => void | Promise<void>;
type FinalCallback = () => void;

type HandleRequestOptions<T> = {
  asyncRequest: AsyncRequest<T>;
  successCallback: SuccessCallback<T>;
  errorCallback?: ErrorCallback;
  finalCallback?: FinalCallback;
  toast?: any;
  erMsg?: string;
  setLoading?: (loading: boolean) => void;
};
const headers = {
  Accept: "*/*",
  "Content-Type": "application/json",
};

const getAuthToken = () => {
  if (typeof window === "undefined") return null;

  return sessionStorage.getItem("jwtToken") || sessionStorage.getItem("temporaryToken") || sessionStorage.getItem("lineAccessToken");
};

//-- 擴充 Axios 介面
declare module "axios" {
  export interface AxiosRequestConfig {
    baseURLType?: ApiEndpoint;
    isAuth?: boolean; //-- 是否需要授權驗證
    /** tracker 用：request 送出時間戳記，response interceptor 算 api_error 的 duration_ms。 */
    _trackerStartTime?: number;
  }
}

/** tracking 自己打的請求路徑；api_error 攔截器要排除，否則 tracking 端點失敗會無限迴圈自己追蹤自己。 */
const isTrackingEndpointUrl = (url?: string): boolean => !!url && url.includes("/tracking/events");

/**
 * Axios timeout (ms)。預設 60 秒；可用 `REACT_APP_API_TIMEOUT` env 覆寫。
 * Local dev 跨區 RDS round-trip 累積容易超過 10s（特別是 submit transaction + trigger 鏈），
 * 因此預設拉長到 60s；prod 部署若想壓緊可在 .env 設較小值。
 *
 * 防呆：env 設成空字串 / 非數字 / 0 / 負數時，fallback 到 60s（`Number("")` 是 0、
 * 設 timeout: 0 對 axios 是無限等，但對「使用者誤設」場景 60s 比較安全）。
 */
const PARSED_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT);
const API_TIMEOUT_MS =
  Number.isFinite(PARSED_TIMEOUT) && PARSED_TIMEOUT > 0 ? PARSED_TIMEOUT : 60000;

const axiosInstance = axios.create({
  baseURL: API_ENDPOINT.LINEBOT,
  timeout: API_TIMEOUT_MS,
  headers,
});

// 設置攔截器，在每個請求中加入token
axiosInstance.interceptors.request.use(
  (config) => {
    // 客服練習模式：改用假後端 adapter，攔截所有請求，保證零真實後端流量。
    // 旗標關閉時（正式客戶）完全不進此分支，行為與原本一致。
    if (isPracticeMode()) {
      config.adapter = resolvePracticeAdapter(config);
      return config;
    }

    // 動態切換 baseURL
    if (config.baseURLType && API_ENDPOINT[config.baseURLType]) {
      config.baseURL = API_ENDPOINT[config.baseURLType];
    }

    // 檔案上傳（FormData）：清掉 axios instance 預設的 application/json Content-Type，
    // 讓瀏覽器依 FormData 自動帶 multipart/form-data + boundary。否則 axios v1 的
    // transformRequest 見 Content-Type=application/json 會呼叫 formDataToJSON 把 FormData
    // 轉成 JSON（Blob→{}），後端 multer 收不到檔 → 上傳簽名/鐵牌報 "file is required"。
    // 注意：此處必須在下方 isAuth===false 的 early return「之前」，因為 upload 帶 isAuth:false，
    // 否則永遠跑不到；且 axios v1 的 config.headers 是 AxiosHeaders，須用 .delete()。
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      const h = config.headers as unknown as { delete?: (key: string) => void };
      if (typeof h?.delete === "function") {
        h.delete("Content-Type");
      }
    }

    if (config?.isAuth === false) {
      return config;
    }

    config.headers = config.headers ?? {};
    // 檢查：如果呼叫時已經有傳 Authorization，就不要覆寫它
    if (config.headers.Authorization) {
      return config;
    }

    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }

    // 確保 Accept header 正確
    config.headers = config.headers ?? {};
    config.headers.Accept = "application/json";

    // 檔案上傳（FormData）：清掉預設的 application/json Content-Type，讓 axios 依 FormData
    // 自動帶 multipart/form-data + boundary。否則 FormData 會被當 JSON 送出（Blob→{}），
    // 後端 multer 收不到檔 → onboarding 上傳簽名/鐵牌報 "file is required"。
    // 注意：axios v1 的 config.headers 是 AxiosHeaders，須用 .delete()；delete headers[key] 無效。
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      const h = config.headers as unknown as { delete?: (key: string) => void };
      if (typeof h.delete === "function") {
        h.delete("Content-Type");
      }
    }

    // tracker：記下送出時間，response interceptor 用來算 api_error 的 duration_ms
    config._trackerStartTime = Date.now();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 添加 response interceptor 來查看錯誤詳情
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const errorContext = {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
    };

    if (error.response) {
      // 伺服器有回應，但狀態碼超出 2xx 範圍 (例如 400, 401, 500)
      Sentry.captureException(error, {
        extra: {
          ...errorContext,
          status: error.response.status,
          responseData: error.response.data, // 伺服器回傳的錯誤細節
        },
        tags: { api_error_type: "server_error" },
      });
    } else if (error.request) {
      Sentry.captureException(new Error(`API Network Error: ${error.config?.url}`), {
        extra: errorContext,
        tags: { api_error_type: "network_error" },
      });
    } else {
      Sentry.captureException(error, {
        tags: { api_error_type: "setup_error" },
      });
    }

    // LINE access token 失效（過期或被撤銷）→ 清掉 LIFF session 重新登入。
    //
    // 放在攔截器而不是各呼叫點：token 只在 LIFF 初始化時取一次、之後不再刷新，「失效」會打到
    // 哪一支 API 純看使用者先點到什麼；光 send-phone-otp 就有六個呼叫點，逐個補等於把同一條
    // 規則抄六份，下一支新 API 又會漏。
    //
    // 判斷不看後端訊息，而是拿 token 去問 LINE 的 verify 端點取權威答案（見 lineSession）：
    // 後端那句 'Failed to verify access token' 是兜底 catch，token 壞掉與後端自己出事共用同一句，
    // 誤判會把使用者無故登出。改用權威判斷後，「未註冊」那種 token 有效的 401 也會自動排除。
    //
    // 不 await：復原是背景動作，錯誤仍照常往下傳給呼叫端；recoverLineSession 內含節流。
    //
    // 練習模式一律跳過：它用假 token，verify 必定回失效 → 會把客服踢去 LINE 登入；
    // 且 verifyLineAccessToken 走原生 fetch，練習模式的假 adapter 攔不到，
    // 會違反「保證零真實後端流量」的契約。（現行練習流程不回 401，這是防未來。）
    //
    // pay-link 模式一律跳過：一次性付款連結頁完全不依賴 LINE 登入（見 PayLinkView），
    // 沒有 lineAccessToken 可言；401 只可能是 pay_token 過期／已使用，走該頁自己的
    // PAY_TOKEN_EXPIRED / PAY_TOKEN_USED 分支，硬跑 recoverLineSession 只會把沒登入
    // 過 LINE 的使用者導去 liff.login() 白白多繞一圈。
    //
    // 本機 skip-LIFF 模式一律跳過：該模式不跑 liff.init()，sessionStorage 也沒有 lineAccessToken，
    // 空字串會被 verifyLineAccessToken 直接判為失效 → 走到未初始化的 liff.login()，
    // 拋出 "You need to define `liffId` for liff.login()" 蓋掉真正的 401 原因。
    if (isUnauthorized(error) && !isPracticeMode() && !isPayLinkMode() && !isDevSkipLiff) {
      void recoverLineSession(sessionStorage.getItem("lineAccessToken") || "");
    }

    // app_tracker：前端視角的 API 錯誤率（行為層、lossy，非權威資料，見 app-tracker-spec §1.1）。
    // 排除 tracking 自身端點，避免 tracking 失敗又送 api_error 追蹤 tracking 失敗的無限迴圈。
    // 不改變原本的錯誤傳遞行為：track() 永不 throw，這裡也不 await，不影響下面的 reject。
    if (!isTrackingEndpointUrl(error.config?.url)) {
      const startedAt = error.config?._trackerStartTime;
      track("api_error", {
        path: error.config?.url,
        method: error.config?.method,
        status: error.response?.status ?? null, // null = 網路錯誤 / timeout（無 response）
        error_code: error.response?.data?.code ?? error.code ?? null,
        duration_ms: typeof startedAt === "number" ? Date.now() - startedAt : null,
      });
    }

    return Promise.reject(error);
  },
);

// 狀況： API 請求成功的情況
// TODO: 不同 http code 的請求狀況
export const handleRequest = async <T>({
  asyncRequest,
  successCallback,
  errorCallback,
  finalCallback,
  toast,
  erMsg,
  setLoading,
}: HandleRequestOptions<T>) => {
  setLoading && setLoading(true);
  try {
    const response = await asyncRequest();

    if (response.status >= 200 && response.status < 300) {
      await successCallback(response);
      return response;
    }
    setLoading && setLoading(false);
    throw response;
  } catch (error: unknown) {
    errorCallback && (await errorCallback(error));
    setLoading && setLoading(false);

    if (toast) {
      let rawMsg = erMsg || "發生錯誤，請稍後再試";
      if (error instanceof Error) {
        rawMsg = erMsg || error.message;
      } else if (typeof error === "string") {
        rawMsg = erMsg || error;
      } else if (error && typeof error === "object" && "message" in error) {
        rawMsg = erMsg || String((error as any).message);
      }
      
      toast.show({
        description: toToastMessage(rawMsg, "發生錯誤，請稍後再試"),
        placement: "top",
      });
    }
  } finally {
    finalCallback && finalCallback();
    setLoading && setLoading(false);
  }
};

export default axiosInstance;
